// ---------------------------------------------------------------------------
// nativeBootstrap — runs once at app startup. When we're hosted inside the
// Capacitor iOS/Android shell, this:
//   • Sets the status bar style to match our light theme
//   • Keeps the native splash up until React has fully mounted AND the first
//     paint has flushed, then fades it out smoothly (no white flash)
//   • Falls back to a hard timeout so the splash can never get stuck
//
// On the web (Vite preview, lovableproject.com, the published site, the PWA),
// this file is a no-op — Capacitor.isNativePlatform() returns false.
// ---------------------------------------------------------------------------

import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { scheduleNudge } from "@/utils/weekendNudge";

const SPLASH_HARD_TIMEOUT_MS = 5000;

let splashHidden = false;

const hideSplashOnce = async () => {
  if (splashHidden) return;
  splashHidden = true;
  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch {
    /* plugin missing or already hidden */
  }
};

const configureStatusBar = async () => {
  try {
    await StatusBar.setStyle({ style: Style.Light }); // dark text on our light bg
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#ffffff" });
    }
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch {
    /* ignore — only matters on native */
  }
};

const waitForFirstPaint = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve();
    // Two RAFs ≈ after layout + after the next paint, which is when the
    // user can actually see something rendered.
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

export const initNativeShell = (): void => {
  if (typeof window === "undefined") return;
  if (!Capacitor?.isNativePlatform?.()) return;

  // Belt-and-braces: even if React crashes during boot, the splash must
  // not stay up forever.
  const safety = window.setTimeout(() => { void hideSplashOnce(); }, SPLASH_HARD_TIMEOUT_MS);

  void configureStatusBar();

  // Hide once the first frame has actually painted.
  waitForFirstPaint().then(async () => {
    await hideSplashOnce();
    window.clearTimeout(safety);
  });

  // Refresh the weekend nudge schedule on every cold-boot so the rotating
  // prompt copy stays fresh (week-of-year picks a new one) and so a
  // reinstall doesn't silently drop the user's setting. No-op when the
  // user has the nudge set to "off." Real-drive note (#69). */
  void scheduleNudge();
};

export const hideNativeSplash = hideSplashOnce;
